/**
 * Precios del contrato y control de consumo.
 * Ver ANALISIS-PLANILLAS.md §3.4 y §3.2, PROMPT-MAESTRO §5.4.
 */

import type { ControlContrato, ItemContrato, NivelSemaforo } from './tipos';

/** Reajuste acumulado del contrato: 13,73%. Fórmula del Excel: ROUND(N*0,1373+N; 3). */
export const FACTOR_REAJUSTE_POR_DEFECTO = 0.1373;

/** IVA vigente en Chile. */
export const IVA = 0.19;

/** Redondeo a `dec` decimales, estable frente a la representación binaria. */
export function roundTo(n: number, dec: number): number {
  const f = 10 ** dec;
  return Math.round((n + Number.EPSILON * Math.sign(n) * Math.abs(n)) * f) / f;
}

/**
 * Precio unitario vigente de una partida.
 * Las partidas marcadas «sin aumento» / «si aumento» en el Excel no se reajustan
 * (Gastos Generales, COVID, Movilización, radios P25, operadores de apoyo).
 */
export function precioVigente(
  item: Pick<ItemContrato, 'precioBaseUf' | 'aplicaReajuste'>,
  factorReajuste: number = FACTOR_REAJUSTE_POR_DEFECTO,
): number {
  if (!item.aplicaReajuste) return item.precioBaseUf;
  return roundTo(item.precioBaseUf * (1 + factorReajuste), 3);
}

/** Convierte UF a pesos con la UF del día. */
export function ufAClp(montoUf: number, valorUf: number): number {
  return Math.round(montoUf * valorUf);
}

/** Descompone un neto en IVA y total a facturar. */
export function conIva(netoClp: number): { neto: number; iva: number; total: number } {
  const iva = Math.round(netoClp * IVA);
  return { neto: netoClp, iva, total: netoClp + iva };
}

/**
 * Control de consumo del contrato — el semáforo que el Excel calculaba en
 * `CARATULA!J30` y que nadie miraba (riesgo R1 del análisis).
 *
 * Con los datos del EP 58 esto debe dar ROJO:
 *   base 289.602 + modificaciones 104.423 − cobrado 416.012,50 = −21.987,50 UF
 */
export function controlarContrato(params: {
  montoBaseUf: number;
  montoModificacionesUf: number;
  cobradoAcumuladoUf: number;
  valorUf: number;
  umbralAmarillo?: number;
}): ControlContrato {
  const { montoBaseUf, montoModificacionesUf, cobradoAcumuladoUf, valorUf } = params;
  const umbralAmarillo = params.umbralAmarillo ?? 0.85;

  const montoVigenteTotalUf = montoBaseUf + montoModificacionesUf;
  const saldoUf = roundTo(montoVigenteTotalUf - cobradoAcumuladoUf, 3);
  const pctConsumo = montoVigenteTotalUf > 0 ? cobradoAcumuladoUf / montoVigenteTotalUf : 0;

  const excedido = saldoUf < 0;
  const excesoUf = excedido ? Math.abs(saldoUf) : 0;
  const excesoClp = ufAClp(excesoUf, valorUf);

  let nivel: NivelSemaforo = 'VERDE';
  if (pctConsumo >= 1) nivel = 'ROJO';
  else if (pctConsumo >= umbralAmarillo) nivel = 'AMARILLO';

  const pct = (pctConsumo * 100).toFixed(1);
  const mensaje = excedido
    ? `Contrato excedido en UF ${excesoUf.toLocaleString('es-CL', { maximumFractionDigits: 2 })} ` +
      `(≈ ${excesoClp.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })}). ` +
      `Requiere modificación de contrato.`
    : nivel === 'AMARILLO'
      ? `Contrato consumido al ${pct}%. Quedan UF ${saldoUf.toLocaleString('es-CL', { maximumFractionDigits: 2 })}.`
      : `Contrato consumido al ${pct}%.`;

  return {
    montoBaseUf,
    montoModificacionesUf,
    montoVigenteTotalUf,
    cobradoAcumuladoUf,
    saldoUf,
    pctConsumo,
    nivel,
    excedido,
    excesoUf,
    excesoClp,
    mensaje,
  };
}
